'use client';

import type { UseFormReturn } from 'react-hook-form';

import { useMutation } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { WidgetId } from '@/edge/constants';

import { toaster } from '@/shared/components/toaster';
import { SERVER_ERROR_CODES } from '@/shared/constants';
import { getUrlToBackendApi, request } from '@/shared/modules/api';
import { useEdgeConfigWidget } from '@/shared/modules/edge-config';
import { getServerError, type HTTPErrorApiResponse } from '@/shared/utils/get-server-error-by-prop';

import { PRIVATE_PROFILE_DATA_QUERY_KEY, PRIVATE_REQUEST_PATHNAME, PROFILE_EDIT_FORM_FIELDS } from '../constants';
import type { ProfileEditFormValues, ProfilePrivateData } from '../types';

import { useProfileContext } from './use-profile-context';
import { useProfileDataQuery } from './use-profile-data-query';

type Params = {
  nickname: string;
  about: string;
};

export const useMutationUpdateUserData = (formMethods: UseFormReturn<ProfileEditFormValues, any, undefined>) => {
  const profileModel = useEdgeConfigWidget(WidgetId.Profile);
  const { refetch } = useProfileDataQuery();
  const { setIsEditMode } = useProfileContext();

  return useMutation<ProfilePrivateData, HTTPError, Params>({
    mutationKey: PRIVATE_PROFILE_DATA_QUERY_KEY,
    mutationFn: (json) => {
      return request.post(getUrlToBackendApi(`${PRIVATE_REQUEST_PATHNAME}/update`), { json }).json();
    },
    onError: (error) => {
      error.response
        .json()
        .then((errorBody: HTTPErrorApiResponse) => {
          const errorByProperty = getServerError(errorBody);
          errorByProperty[SERVER_ERROR_CODES.common] && toaster.error(errorByProperty[SERVER_ERROR_CODES.common]);
          Object.entries(PROFILE_EDIT_FORM_FIELDS).forEach(
            ([key, value]: [string, (typeof PROFILE_EDIT_FORM_FIELDS)[keyof typeof PROFILE_EDIT_FORM_FIELDS]]) => {
              // @todo remove lowerCase after BE fix
              if (errorByProperty[value.toLowerCase()]) {
                formMethods.setError(value, { message: errorByProperty[value.toLowerCase()] });
              }
            },
          );
        })
        .catch((e: Error) => toaster.error(e.message));
    },
    onSuccess: () => {
      refetch();
      setIsEditMode(false);
      toaster.success(profileModel.strings.save);
    },
  });
};
